import * as Mui from  "@mui/material";
import FormInput from "../../../common/FormInput";
import CloseIcon from '@mui/icons-material/Close';
import FileUploader from "../../../../../../util/component/FileUploader";
import { useContext, useEffect, useMemo, useState } from "react";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import DamageFormService from "../../service/DamageFormService";
import { ContextOperation } from "../../../../context/OperationContext";
import { useForm } from "react-hook-form";
import { CreateDamageFormTypes, DamageFormTypes } from "../../Types";
import { nameof } from "../../../common/ObjectUtils";
import { useLocation } from "react-router-dom";
import OperationTypes from "../../../../types";
import useSnackbar from "../../../../hooks/useSnackbar";
import { useTranslation } from "react-i18next";

interface CreateDamageForm {
    partId: number; // ID della parte selezionata
    positionId: number; // ID della posizione della parte selezionata
    severity: string; // Severity selezionata
}

interface Props {
    open: boolean;
    onClose: () => void;
    onCreateDamage: (damageSaved: CreateDamageFormTypes.IRespCreateDamage) => void;
    parts: OperationTypes.VehiclePart[];
    severities: string[];
}
const CreateDamage = (props: Props) => {
    const t = useOperationTranslation();
    const {i18n} = useTranslation(['operation']);
    const { tenant, setLoading } = useContext(ContextOperation);

    const locationState = useLocation().state as CreateDamageFormTypes.ISharedLocationState;

    const form = useForm<CreateDamageForm>();

    const snackbar = useSnackbar();

    const parts = useMemo(() => {
        return props.parts.map((part) => ({
            //label: part.label,
            label: t(`masterDataDamage.partName.${part.part.code}` as any)  + (part.position.code != "6" ?  " " + t(`masterDataDamage.positionName.${part.position.code}` as any) : ""),
            value: `${part.part.id}.${part.position.id}`,
        }));
    }, [props.parts]);

    const severities = useMemo(() => {
        return props.severities.map((sev) => ({
            label: t(`damageList.severityValue.${sev?.toLowerCase()}` as any),
            value: sev
        }));
    }, [props.severities, i18n.language]);

    const [fileUploadedOne, setFileUploadedOne] = useState<File | null>(null);
    const [fileUploadedTwo, setFileUploadedTwo] = useState<File | null>(null);

    const onFileUpload = (file: File, photoNumber: number) => {
        // const data = new FormData();
        // data.append("files", file);

        if (photoNumber === 1) {
            setFileUploadedOne(file);
        } else if (photoNumber === 2) {
            setFileUploadedTwo(file);
        }
    }

    const onSubmit = (data: CreateDamageForm) => {
        setLoading(true);

        DamageFormService.create.post.addOpen(tenant, {
            damageList: [
                {
                    partId: data.partId,
                    positionId: data.positionId,
                    severity: data.severity,
                    damageDate: locationState.generalInfo?.eventDate,
                },
            ],
            vehiclePlate: locationState.generalInfo!.plate,
        }).then(
            response => {
                if (!fileUploadedOne && !fileUploadedTwo) {
                    props.onCreateDamage(response[0]);
                    onClose();
                    setLoading(false);
                } else {
                    DamageFormService.create.post.addPhoto(response[0].id, [fileUploadedOne, fileUploadedTwo]).then(
                        responsePhoto => {
                            setLoading(false);

                            if (fileUploadedOne) {
                                
                                response[0].firstPhotoId = responsePhoto[0].id;

                                if (fileUploadedTwo) {
                                    response[0].secondPhotoId = responsePhoto[1].id;
                                }

                            } else {
                                if (fileUploadedTwo) {
                                    response[0].secondPhotoId = responsePhoto[0].id;
                                }
                            }
                            
                            props.onCreateDamage(response[0]);

                            setFileUploadedOne(null);
                            setFileUploadedTwo(null);
    
                            onClose();
                        },
                        error => {
                            setLoading(false);
                        }
                    )
                }
            }
        ).catch((error) => {
            setLoading(false);
            
            if (error?.response?.status === 409) {
                snackbar.show("error", t("damageForm.dialogs.create.damageExistsError"));
            } else {
                snackbar.show("error", t("damageList.damageCreation.message.errorDamagesCreation"));            
            }

          });;
    }

    const onClose = () => {
        form.reset();
        props.onClose();
    }
    
    return(
        <Mui.Dialog
            open={props.open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <Mui.DialogTitle>
                <Mui.Grid container alignItems={"center"}>
                    <Mui.Grid item>
                        <Mui.Typography variant="h5">
                            {t("damageForm.damageFormCreation.dialogs.createDamage.title")}
                        </Mui.Typography>
                    </Mui.Grid>
                    <Mui.Grid item marginLeft={"auto"}>
                        <Mui.IconButton onClick={onClose}>
                            <CloseIcon />
                        </Mui.IconButton>
                    </Mui.Grid>
                </Mui.Grid>
            </Mui.DialogTitle>
            
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Mui.DialogContent>
                    <Mui.Grid container spacing={3} mb={3}>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<CreateDamageForm>("partId")}
                                type="select"
                                options={parts}
                                fullWidth
                                formInstance={form}
                                label={t("damageForm.damageFormCreation.dialogs.createDamage.vehiclePart")}
                                value={`${form.watch("partId")}.${form.watch("positionId")}`}
                                onChange={(val) => {
                                    form.setValue("partId", parseInt(val?.split(".")?.[0]));
                                    form.setValue("positionId", parseInt(val?.split(".")?.[1]));
                                }}
                                validation={{
                                    required: t("damageForm.damageFormCreation.dialogs.createDamage.required")
                                }}
                                error={!form.formState.errors.partId ? true : false}
                                helperText={form.formState.errors.partId?.message}
                            />
                        </Mui.Grid>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<CreateDamageForm>("severity")}
                                type="select"
                                options={severities}
                                formInstance={form}
                                fullWidth
                                label={t("damageForm.damageFormCreation.dialogs.createDamage.severity")}
                                validation={{
                                    required: t("damageForm.damageFormCreation.dialogs.createDamage.required")
                                }}
                            />
                        </Mui.Grid>
                    </Mui.Grid>

                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 1)}
                        onRemove={() => {console.log("remove")}}
                    />
                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 2)}
                        onRemove={() => {console.log("remove")}}
                    />
                </Mui.DialogContent>

                <Mui.DialogActions>
                    <Mui.Button variant="outlined" size="smallPlus" onClick={onClose}>
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageForm.damageFormCreation.button.cancel")}
                        </Mui.Typography>
                    </Mui.Button>
                    <Mui.Button type="submit" size="smallPlus">
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageForm.damageFormCreation.button.save")}
                        </Mui.Typography>
                    </Mui.Button>
                </Mui.DialogActions>
            </form>

        </Mui.Dialog>
    )
}
export default CreateDamage;